import { ServerTimeObservation } from './corrected-clock'

/**
 * What the page shell told us about the server's clock, and what the local
 * clock read beside it.
 */
export interface PageTimeReadings {
    /** `__gameplan_server_time`, rendered into the shell. Undefined if the shell did not carry one. */
    serverUtcMs: number | undefined;
    /** The local clock when the shell script ran. */
    clientUtcMs: number;
    /** Whether the navigation response came out of the browser's cache. */
    navigationWasCached: boolean;
}

/**
 * Deliberately generous: we do not measure how long the page took to reach the
 * browser, and the value was stamped before it was sent. The point of this
 * source is to be roughly right *before the first id exists*, which is the one
 * moment a fast client clock can be corrected at all — not to be precise. The
 * SignalR sync overtakes it within about a second.
 */
const PAGE_TIME_UNCERTAINTY_MS = 2000;

/**
 * Reads what the shell left on `window`. Defensive throughout: bundles run
 * under jasmine and inside workers where none of these globals exist, and a
 * clock that throws on load would take the whole app with it.
 */
export function readPageTimings(): PageTimeReadings {

    let serverUtcMs: number | undefined;
    let clientUtcMs = Date.now();

    try {
        if (typeof __gameplan_server_time === 'number') {
            serverUtcMs = __gameplan_server_time;
        }
        // Pair the server's reading with the local one taken beside it in the
        // same script, not with the local clock now: anything between the two
        // would land in the offset as though it were skew.
        if (typeof __gameplan_initialisation_time !== 'undefined' && __gameplan_initialisation_time) {
            clientUtcMs = __gameplan_initialisation_time.getTime();
        }
    } catch (e) {
        // Not running under a rendered shell. serverUtcMs stays undefined and
        // observationFromPage declines.
    }

    return { serverUtcMs, clientUtcMs, navigationWasCached: navigationWasCached() };
}

function navigationWasCached(): boolean {
    try {
        const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        const navigation = entries && entries[0];
        // No entry means the browser will not say. Treating that as "not
        // cached" is the right way round: the HTTP `Date` cross-check corrects
        // a stale reading, whereas declining outright would leave a fast client
        // clock uncorrectable for the whole session.
        if (!navigation) return false;
        return navigation.transferSize === 0;
    } catch (e) {
        return false;
    }
}

export function observationFromPage(readings: PageTimeReadings): ServerTimeObservation | null {

    if (typeof readings.serverUtcMs !== 'number' || !isFinite(readings.serverUtcMs)) {
        return null;
    }

    // A cached shell carries the time it was stored rather than the time it is
    // being read, and nothing distinguishes a stale page from a client clock
    // running fast by the same amount. The one source that gets to correct a
    // fast clock is the one that must not be fooled into inventing one.
    if (readings.navigationWasCached) {
        return null;
    }

    return {
        serverUtcMs: readings.serverUtcMs,
        clientUtcMs: readings.clientUtcMs,
        uncertaintyMs: PAGE_TIME_UNCERTAINTY_MS,
    };
}
